import React, { useEffect, useRef, useState } from 'react';
import AuthService from '../services/authService';
import AccountVerifiedModal from './AccountVerifiedModal';
import { formatAccountNumberForDisplay } from '../utils/platformAccount';

/**
 * App-level watcher: polls the user's platform accounts in the background and,
 * whenever an account's verification status changes, shows a popup — no matter
 * which screen is currently open. Mounted once (only while authenticated) so it
 * outlives navigation between screens.
 */

const POLL_INTERVAL_MS = 10000;

interface PlatformAccount {
    id: number;
    platform_key?: string;
    platform_name: string;
    casino_user_id: string;
    is_verified?: boolean;
    status?: string;
    verification_status?: string;
    account_status?: string;
    [key: string]: any;
}

// `status` is the source of truth; is_verified is the fallback.
const normalizeStatus = (a: PlatformAccount): string => {
    const raw = a.status ?? a.verification_status ?? a.account_status;
    const s = String(raw ?? '').trim().toLowerCase().replace(/\s+/g, '_');
    if (s) return s;
    return a.is_verified ? 'verified' : 'not_verified';
};

export default function PlatformStatusWatcher() {
    // Previous id -> status snapshot. Null until the baseline is established, so
    // the very first fetch never pops (only real transitions do).
    const snapshotRef = useRef<Map<number, string> | null>(null);
    const [popup, setPopup] = useState<{
        visible: boolean;
        accountNumber: string;
        platformName: string;
        verified: boolean;
    }>({ visible: false, accountNumber: '', platformName: '', verified: true });

    useEffect(() => {
        let active = true;

        const poll = async () => {
            try {
                const res = await AuthService.getPlatformAccounts();
                const list: PlatformAccount[] =
                    res?.status === 'success' && Array.isArray(res.data) ? res.data : [];
                if (!active) return;

                const current = new Map(list.map((a) => [a.id, normalizeStatus(a)]));
                const prev = snapshotRef.current;

                if (prev) {
                    // Status changes on accounts that existed in the last snapshot.
                    const changed = list.filter(
                        (a) => prev.has(a.id) && prev.get(a.id) !== normalizeStatus(a)
                    );
                    if (changed.length) {
                        const target =
                            changed.find((a) => normalizeStatus(a) === 'verified') || changed[0];
                        setPopup({
                            visible: true,
                            accountNumber: formatAccountNumberForDisplay(
                                target.casino_user_id,
                                target.platform_key || target.platform_name
                            ),
                            platformName: target.platform_name,
                            verified: normalizeStatus(target) === 'verified',
                        });
                    }
                }

                snapshotRef.current = current;
            } catch (_) {
                // Ignore transient poll errors.
            }
        };

        poll(); // establish baseline immediately
        const interval = setInterval(poll, POLL_INTERVAL_MS);
        return () => {
            active = false;
            clearInterval(interval);
        };
    }, []);

    return (
        <AccountVerifiedModal
            visible={popup.visible}
            accountNumber={popup.accountNumber}
            platformName={popup.platformName}
            verified={popup.verified}
            onGotIt={() => setPopup((p) => ({ ...p, visible: false }))}
        />
    );
}